import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScanLine, Loader2, Search, AlertCircle } from "lucide-react";
import { fmt2 } from "@/lib/utils";

/**
 * Stock-readjust dialog triggered by scanning an internal batch code
 * (e.g. INT-20260614-0003) on the dashboard. If the scanner emits a
 * garbled / unrecognised string the dialog stays open with an editable
 * lookup field so the operator can type / re-scan the code without
 * reopening anything. On a successful lookup it shows the intake
 * snapshot and lets them type the physical count — the backend bumps
 * `quantity` automatically if the count exceeds the recorded total.
 */
export default function StockAdjustDialog({ scannedCode, onClose, onSaved }) {
  const [lookupCode, setLookupCode] = useState("");
  const [intake, setIntake] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [remaining, setRemaining] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const codeInputRef = useRef(null);

  // When the parent opens the dialog with a fresh scanned code,
  // prefill the lookup field and run the search.
  useEffect(() => {
    if (!scannedCode) {
      setLookupCode("");
      setIntake(null);
      setNotFound(false);
      setRemaining("");
      setNotes("");
      return;
    }
    setLookupCode(scannedCode);
    runLookup(scannedCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannedCode]);

  const runLookup = (raw) => {
    const trimmed = String(raw || "").trim();
    if (!trimmed) {
      setNotFound(true);
      setIntake(null);
      return;
    }
    setLoading(true);
    setNotFound(false);
    api
      .get(`/intakes/by-code/${encodeURIComponent(trimmed)}`)
      .then((r) => {
        setIntake(r.data);
        setRemaining(String(r.data.remaining ?? ""));
        setNotes("");
      })
      .catch((err) => {
        setIntake(null);
        setNotFound(true);
        // Keep the dialog open so the user can see what was scanned +
        // retype. Also show a toast so they get feedback outside the
        // dialog in case it was dismissed.
        toast.error(
          err?.response?.data?.detail || `No match for "${trimmed}"`
        );
        // Focus the input so a quick re-scan replaces the value.
        setTimeout(() => codeInputRef.current?.select?.(), 50);
      })
      .finally(() => setLoading(false));
  };

  const save = async () => {
    const n = Number(remaining);
    if (!Number.isFinite(n) || n < 0) {
      toast.error("Enter a valid remaining amount");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/intakes/${intake.id}/stock`, {
        remaining: n,
        notes,
      });
      toast.success(
        `Stock readjusted · ${intake.material_name} now ${fmt2(n)} ${intake.unit}`
      );
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to adjust stock");
    } finally {
      setSaving(false);
    }
  };

  const open = Boolean(scannedCode);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="stock-adjust-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ScanLine className="h-5 w-5 text-primary" /> Readjust stock
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Always-visible lookup field so an unmatched scan is editable */}
          <div>
            <Label className="text-sm">Scanned / typed code</Label>
            <div className="mt-1 flex gap-2">
              <Input
                ref={codeInputRef}
                className="h-12 font-mono"
                value={lookupCode}
                onChange={(e) => setLookupCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    runLookup(lookupCode);
                  }
                }}
                data-testid="stock-adjust-code-input"
                placeholder="e.g. INT-20260614-0003"
              />
              <Button
                type="button"
                variant="outline"
                className="h-12 px-3 gap-1"
                onClick={() => runLookup(lookupCode)}
                data-testid="stock-adjust-lookup"
              >
                <Search className="h-4 w-4" /> Find
              </Button>
            </div>
            <div className="text-[11px] text-stone-500 mt-1">
              Scan the printed INT- label (or the supplier batch) — the match
              runs automatically. Type and tap Find if the scanner garbles it.
            </div>
          </div>

          {loading && (
            <div className="flex items-center gap-2 text-sm text-stone-500 py-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Looking up {lookupCode}…
            </div>
          )}

          {!loading && notFound && !intake && (
            <div
              className="rounded-lg border border-rose-200 bg-rose-50 dark:bg-rose-950/30 p-3 flex items-start gap-2"
              data-testid="stock-adjust-notfound"
            >
              <AlertCircle className="h-4 w-4 text-rose-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-rose-800 dark:text-rose-200">
                No intake matches "<span className="font-mono">{lookupCode}</span>".
                Check the label, retype or rescan.
              </div>
            </div>
          )}

          {!loading && intake && (
            <>
              <div
                className="rounded-lg border border-border bg-stone-50 dark:bg-stone-900/50 p-3"
                data-testid="stock-adjust-body"
              >
                <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">
                  Internal batch
                </div>
                <div
                  className="font-mono text-sm"
                  data-testid="stock-adjust-code"
                >
                  {intake.internal_batch_code}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                  <div className="col-span-2">
                    <div className="text-[10px] uppercase tracking-wider text-stone-500">
                      Material
                    </div>
                    <div className="font-semibold">{intake.material_name}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-stone-500">
                      Supplier
                    </div>
                    <div>{intake.supplier_name}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-stone-500">
                      Supplier batch
                    </div>
                    <div className="font-mono">{intake.supplier_batch}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-stone-500">
                      Received total
                    </div>
                    <div className="tnum">
                      {fmt2(intake.quantity)} {intake.unit}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-stone-500">
                      Current remaining
                    </div>
                    <div className="tnum font-semibold">
                      {fmt2(intake.remaining)} {intake.unit}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-sm">
                  New remaining ({intake.unit})
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  className="h-12 mt-1"
                  value={remaining}
                  onChange={(e) => setRemaining(e.target.value)}
                  data-testid="stock-adjust-remaining"
                  autoFocus
                />
                <div className="text-[11px] text-stone-500 mt-1">
                  Enter the physical count. If it's above the received total,
                  the record will auto-expand so the stock figure stays
                  accurate.
                </div>
              </div>

              <div>
                <Label className="text-sm">Reason / notes (optional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="stock-adjust-notes"
                  placeholder="e.g. recount after cleaning, spill, usage not logged"
                  rows={2}
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            data-testid="stock-adjust-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={!intake || saving}
            data-testid="stock-adjust-save"
            className="h-12 px-6"
          >
            {saving ? "Saving…" : "Save count"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
